import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RankingModal from '../components/RankingModal';
import '../styles/RankingPage.css';
import '../styles/common.css';

// サムネイルコンポーネント
const RankingThumbnail = ({ src, alt, lazy }) => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <>
      {isLoading && (
        <div className="skeleton-loader" />
      )}
      <div className="ranking-thumbnail-bg" style={{ display: isLoading ? 'none' : 'block' }}>
        <img 
          src={src} 
          alt="" 
          loading={lazy ? "lazy" : undefined}
        />
      </div>
      <img 
        src={src} 
        alt={alt} 
        loading={lazy ? "lazy" : undefined}
        onLoad={() => setIsLoading(false)}
        className="skeleton-image"
        style={{ opacity: isLoading ? 0 : 1 }}
      />
    </>
  );
};

const RankingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // URLパラメータのデフォルト値
  const DEFAULT_PARAMS = {
    period: '1day',
    type: 'views',
    platform: 'youtube'
  };
  
  // URLパラメータから現在の値を取得
  const getUrlParams = () => {
    const searchParams = new URLSearchParams(location.search);
    let period = searchParams.get('period') || DEFAULT_PARAMS.period;
    let type = searchParams.get('type') || DEFAULT_PARAMS.type;
    let platform = searchParams.get('platform') || DEFAULT_PARAMS.platform;
    
    // 有効な値の検証
    const validPeriods = ['1day', '7day'];
    const validTypes = ['views', 'concurrent'];
    const validPlatforms = ['youtube', 'twitcasting'];
    
    // 値の検証とデフォルト値への変換
    period = validPeriods.includes(period) ? period : DEFAULT_PARAMS.period;
    platform = validPlatforms.includes(platform) ? platform : DEFAULT_PARAMS.platform;
    type = validTypes.includes(type) ? type : DEFAULT_PARAMS.type;
    
    
    return {
      period: period,
      type: type,
      platform: platform
    };
  };
  
  const urlParams = getUrlParams();
  
  const [rankingData, setRankingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(urlParams.period);
  const [selectedType, setSelectedType] = useState(urlParams.type);
  const [selectedPlatform, setSelectedPlatform] = useState(urlParams.platform);
  const [selectedRankingItem, setSelectedRankingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 外部JSONのデータをアプリケーションの形式にマッピング
  const mapRankingData = (externalData) => {
    if (!externalData || !externalData.rankings || !Array.isArray(externalData.rankings)) {
      return null;
    }

    const mappedRankings = {
      concurrent: {
        '1day': [],
        '7day': []
      },
      views: {
        '1day': [],
        '7day': []
      }
    };

    // 外部JSONの各ランキングを処理
    externalData.rankings.forEach(ranking => {
      const period = ranking.period === '1' ? '1day' : '7day';
      const type = ranking.type === 'view' ? 'views' : 'concurrent';
      
      mappedRankings[type][period] = ranking.items.map(item => ({
        // チャンネル情報
        channelName: item.channelTitle,
        channelThumbnail: item.channelThumbnails?.high?.url || 
                         item.channelThumbnails?.medium?.url || 
                         item.channelThumbnails?.default?.url,
        channelUrl: item.channelUrl || null, // 外部JSONには含まれていない
        
        // 動画情報
        streamTitle: item.title,
        videoUrl: item.videoUrl,
        videoThumbnail: item.videoThumbnails?.maxres?.url ||
                       item.videoThumbnails?.standard?.url ||
                       item.videoThumbnails?.high?.url ||
                       item.videoThumbnails?.medium?.url ||
                       item.videoThumbnails?.default?.url ||
                       item.videoThumbnails?.large?.url ||
                       item.videoThumbnails?.small?.url,
        
        // 統計情報 - maxViewは両方のタイプで使用される（ハイフン対応）
        maxConcurrent: type === 'concurrent' ? item.maxView : 0,
        totalViews: type === 'views' ? item.maxView : 0,
        
        // その他の情報
        streamDate: item.publishedDate,
        serviceKind: item.serviceKind,
        duration: item.duration,
        description: item.description || '',
        status: item.status || 'completed'
      }));
    });

    return {
      rankings: mappedRankings,
      availablePeriods: ['1day', '7day'],
      lastUpdated: externalData.lastUpdated
    };
  };

  // URLパラメータの変更を監視
  useEffect(() => {
    const params = getUrlParams();
    
    setSelectedPeriod(params.period);
    setSelectedType(params.type);
    setSelectedPlatform(params.platform);
  }, [location.search]);
  
  // パラメータ変更時にURLを更新
  const updateUrlParams = (period, type, platform) => {
    navigate(`/ranking?period=${period}&type=${type}&platform=${platform}`, { replace: true });
  };
  
  // プラットフォームの表示名を取得
  const getPlatformDisplayName = (platform) => {
    return platform === 'twitcasting' ? 'ツイキャス' : 'YouTube';
  };
  
  // データ取得関数
  const loadRankingData = async (platform) => {
    setLoading(true);
    try {
      // 本番環境判定
      const isProduction = window.location.hostname === 'schedule.stpr.com';
      const filename = platform === 'twitcasting' ? 'twitcasting_ranking.json' : 'ranking.json';
      const RANKING_API_URL = isProduction
        ? `https://schedule-share-json.s3.ap-northeast-1.amazonaws.com/schedule/${filename}`
        : `https://dev-schedule-share-json.s3.ap-northeast-1.amazonaws.com/schedule/${filename}`;
      
      const response = await fetch(RANKING_API_URL);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const externalData = await response.json();
      
      // データ構造の検証
      if (!externalData || !externalData.rankings || !Array.isArray(externalData.rankings)) {
        throw new Error('Invalid data structure');
      }
      
      // データをマッピング
      const mappedData = mapRankingData(externalData);
      
      if (mappedData) {
        setRankingData(mappedData);
      } else {
        throw new Error('Failed to map ranking data');
      }
    } catch (error) {
      console.error('ランキングデータの取得に失敗しました:', error);
      // エラー時のフォールバック
      setRankingData({
        rankings: {
          concurrent: { '1day': [], '7day': [] },
          views: { '1day': [], '7day': [] }
        },
        availablePeriods: ['1day', '7day'],
        lastUpdated: null
      });
    } finally {
      setLoading(false);
    }
  };

  // 数値フォーマット関数（ハイフン対応）
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    if (num === '-' || num === '－') return '-';
    if (typeof num === 'string') return num;
    return num.toLocaleString('ja-JP');
  };

  // 日時フォーマット関数
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };


  // プラットフォーム変更時にデータを再取得
  useEffect(() => {
    loadRankingData(selectedPlatform);
  }, [selectedPlatform]);

  // 現在のランキングデータを取得
  const getCurrentRankingData = () => {
    if (!rankingData) return [];
    return rankingData.rankings[selectedType]?.[selectedPeriod] || [];
  };

  // ランキングアイテムクリックハンドラー
  const handleRankingItemClick = (item, rank, event) => {
    const selection = window.getSelection();
    
    // クリックした要素が画像の場合は、テキスト選択に関わらず処理を続行
    if (event && event.target.tagName === 'IMG') {
      const itemWithRank = { ...item, rank: rank + 1 };
      setSelectedRankingItem(itemWithRank);
      setIsModalOpen(true);
      return;
    }
    
    // テキスト選択中の場合はモーダルを開かない
    if (selection && selection.toString().length > 0) {
      return;
    }
    
    const itemWithRank = { ...item, rank: rank + 1 };
    setSelectedRankingItem(itemWithRank);
    setIsModalOpen(true);
  };

  // モーダルを閉じる
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRankingItem(null);
  };

  // ブックマークハンドラー
  // const handleBookmark = (e, item) => {
  //   e.stopPropagation();
  //   console.log('ブックマーク:', item.channelName);
  //   // TODO: ブックマーク機能の実装
  // };

  // ツイートハンドラー
  // const handleTweet = (e, item) => {
  //   e.stopPropagation();
  //   const tweetText = `${item.streamTitle} - ${item.channelName}`;
  //   const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
  //   window.open(tweetUrl, '_blank');
  // };

  return (
    <div className="ranking-page-container">

      {loading ? (
        <div className="ranking-loading-message">
          <p>ランキングデータを読み込み中...</p>
        </div>
      ) : (
        <div className="ranking-content">
          <div 
            className="ranking-list"
            key={`${selectedPeriod}-${selectedType}`}
            data-period={selectedPeriod}
            data-type={selectedType}
          >
            {getCurrentRankingData().map((item, index) => (
              <div 
                key={`${index}-${item.channelName}`}
                className="ranking-item"
                onClick={(e) => handleRankingItemClick(item, index, e)}
              >
                {/* ステータスバッジを右上に追加 */}
                {(item.status === 'protected' || item.status === 'members_only') && (
                  <div className="ranking-status-badges">
                    {item.status === 'protected' && (
                      <span className="stream-status-badge protected">プライベート配信</span>
                    )}
                    {item.status === 'members_only' && (
                      <span className="stream-status-badge members-only">メンバー限定</span>
                    )}
                  </div>
                )}
                
                <div className="ranking-thumbnail">
                  <RankingThumbnail 
                    src={item.videoThumbnail || item.channelThumbnail}
                    alt={item.channelName}
                    lazy={index >= 10}
                  />
                </div>
                <div className="ranking-content-area">
                  <div className="ranking-viewers">
                    <div className="ranking-viewer-count">
                      {formatNumber(item.maxConcurrent || item.totalViews)}
                    </div>
                    <div className="ranking-viewer-label">{selectedType === 'views' && selectedPlatform === 'youtube' ? 'view' : 'viewers'}</div>
                  </div>
                  <div className="ranking-info">
                    <div className="ranking-title">{item.streamTitle}</div>
                    <div className="ranking-streamer-name">
                      <img src={item.channelThumbnail} alt={item.channelName} className="ranking-channel-icon" loading={index >= 10 ? "lazy" : undefined} />
                      {item.channelName}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {rankingData?.lastUpdated && (
            <div className="ranking-last-updated">
              最終更新: {formatDate(rankingData.lastUpdated)}
            </div>
          )}
        </div>
      )}

      {/* ランキングモーダル */}
      <RankingModal
        rankingItem={selectedRankingItem}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default RankingPage;
